class CustomCartDrawer extends HTMLElement {
    constructor() {
        super();

        this.openTrigger = document.querySelector(`#${this.dataset.openTrigger}`);
        this.overlay = this.querySelector("#CustomCartOverlay");
        this.closeButton = this.querySelector("[data-close]");
        this.liveRegion = this.querySelector("#CartLiveRegion");
        this.cartCount = document.querySelector(".cart-items-count")
        this.previouslyFocused = null;
    }

    connectedCallback() {
        this.openTrigger?.addEventListener("click", this.handleOpen.bind(this));
        this.closeButton?.addEventListener("click", this.closeDrawer.bind(this));
        document.addEventListener("click", (event) => {
            if(event.target === this.overlay) {
                this.closeDrawer();
            }
        })

        // Delegated (not bound to the checkbox itself) so it keeps working
        // after cartRerender() replaces this.innerHTML on add/qty-change.
        this.addEventListener("change", this.handleGiftBoxChange.bind(this));
        this.addEventListener("click", this.handleRecommendationAdd.bind(this));

        // Escape-to-close + Tab focus trap while open. Document-level so
        // Escape is always caught; the Tab trap self-guards on open state
        // and re-queries focusable elements each press (cheap, and safe
        // against cartRerender() swapping the DOM out from under a cached
        // list).
        document.addEventListener("keydown", this.handleKeydown.bind(this));

        document.addEventListener("cart:rerender", this.cartRerender.bind(this));
    }

    getFocusableElements() {
        return Array.from(
            this.querySelectorAll(
                'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => el.offsetParent !== null);
    }

    handleKeydown(event) {
        if (!this.hasAttribute("open")) return;

        if (event.key === "Escape" || event.key === "Esc") {
            event.preventDefault();
            this.closeDrawer();
            return;
        }

        if (event.key !== "Tab") return;

        const focusable = this.getFocusableElements();
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }

    handleGiftBoxChange(event) {
        if (event.target.id !== "CartGiftBox") return;

        const checked = event.target.checked;

        // Cart attributes persist through to checkout/order — no need to
        // re-render the drawer for this, only the item list/subtotal do.
        fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                attributes: {
                    'Gift Box': checked ? 'Yes' : 'No'
                }
            }),
        })
        .catch((error) => {
            console.error('Error updating gift box preference:', error);
        });
    }

    handleRecommendationAdd(event) {
        const button = event.target.closest("[data-rec-add]");
        if (!button || button.classList.contains("loading")) return;

        button.classList.add("loading");
        button.disabled = true;

        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                items: [{
                    id: button.dataset.variantId,
                    quantity: 1
                }],
                sections: "custom-cart-drawer,custom-cart-count"
            }),
        })
        .then((response) => response.json())
        .then((data) => {
            // On success cartRerender() replaces this.innerHTML entirely
            // (including this button), so no need to manually reset its
            // loading state there — only on failure, below.
            document.documentElement.dispatchEvent(
                new CustomEvent("cart:rerender", {
                    detail: data,
                    bubbles: true
                })
            );
        })
        .catch((error) => {
            console.error('Error adding recommended item:', error);
            button.classList.remove("loading");
            button.disabled = false;
        });
    }

    // Shopify only populates `recommendations.products` when a section is
    // reached through this dedicated endpoint (not a normal render or the
    // generic Section Rendering API) — same mechanism
    // scripts/components/product-recommendations.js already uses for the
    // "You may also like" carousel, just fetching the compact
    // sections/cart-recommendations.liquid instead. Based on the most
    // recently added item, so cart.js is fetched first to find it —
    // avoids relying on stale event data across the add/change/remove
    // paths, which return differently-shaped responses.
    loadRecommendations() {
        const container = this.querySelector("#CartRecommendations");
        if (!container) return;

        fetch(window.Shopify.routes.root + 'cart.js')
            .then((response) => response.json())
            .then((cart) => {
                if (!cart.items.length) {
                    container.innerHTML = "";
                    return;
                }

                const lastItem = cart.items[cart.items.length - 1];
                this.fetchRecommendations(lastItem.product_id, "complementary")
                    .then((recsHtml) => {
                        // "complementary" ("bought together") needs real
                        // order co-occurrence data — on a store without
                        // enough order history yet it comes back empty for
                        // most products. Fall back to the default
                        // (attribute/collection similarity), which works
                        // even with zero order history, rather than the
                        // section silently showing nothing.
                        if (recsHtml) return recsHtml;
                        return this.fetchRecommendations(lastItem.product_id);
                    })
                    .then((recsHtml) => {
                        // re-query — cartRerender() may have replaced
                        // this.innerHTML again while these fetches were in
                        // flight
                        const freshContainer = this.querySelector("#CartRecommendations");
                        if (freshContainer) {
                            freshContainer.innerHTML = recsHtml || "";
                        }
                    });
            })
            .catch((error) => {
                console.error('Error loading cart recommendations:', error);
            });
    }

    // Returns the .x-cart__recs markup as a string, or null if that
    // intent/product combination came back empty.
    fetchRecommendations(productId, intent) {
        let url = window.Shopify.routes.root +
            'recommendations/products?section_id=cart-recommendations&limit=4&product_id=' +
            productId;
        if (intent) url += '&intent=' + intent;

        return fetch(url)
            .then((response) => response.text())
            .then((html) => {
                const temp = document.createElement("div");
                temp.innerHTML = html;
                const recs = temp.querySelector(".x-cart__recs");
                return recs ? recs.outerHTML : null;
            });
    }

    handleOpen(event) {
        event.preventDefault();
        this.openDrawer();
    }

    openDrawer() {
        this.previouslyFocused = document.activeElement;
        this.removeAttribute("inert");
        this.setAttribute("open", "");
        this.openTrigger?.setAttribute("aria-expanded", "true");
        this.hideForeignFixedWidgets();
        this.lockBodyScroll();
        this.loadRecommendations();

        // wait a frame so the drawer is actually interactive/visible
        // before moving focus into it
        requestAnimationFrame(() => {
            this.closeButton?.focus();
        });
    }

    closeDrawer() {
        this.removeAttribute("open");
        this.setAttribute("inert", "");
        this.openTrigger?.setAttribute("aria-expanded", "false");
        this.restoreForeignFixedWidgets();
        this.unlockBodyScroll();

        if (this.previouslyFocused && typeof this.previouslyFocused.focus === "function") {
            this.previouslyFocused.focus();
        }
        this.previouslyFocused = null;
    }

    // overscroll-behavior:contain on .x-cart__body (see the CSS) stops
    // scroll chaining in most browsers, but not reliably everywhere
    // (iOS Safari in particular) — this is the real fix: freeze the
    // page itself via position:fixed while the drawer is open, so there
    // is nothing behind it left to scroll. Guarded so repeat calls (e.g.
    // cartRerender() calling openDrawer() again on every add/qty-change
    // while already open) don't re-snapshot scrollY mid-lock.
    lockBodyScroll() {
        if (document.body.dataset.cartDrawerScrollLocked) return;

        this.lockedScrollY = window.scrollY;
        document.body.dataset.cartDrawerScrollLocked = "true";
        document.body.style.position = "fixed";
        document.body.style.top = `-${this.lockedScrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
    }

    unlockBodyScroll() {
        if (!document.body.dataset.cartDrawerScrollLocked) return;

        delete document.body.dataset.cartDrawerScrollLocked;
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.left = "";
        document.body.style.right = "";
        document.body.style.width = "";
        window.scrollTo(0, this.lockedScrollY || 0);
    }

    // Even at the literal CSS z-index max (2147483647), a third-party
    // floating widget (chat/accessibility launcher — injected by an
    // installed app, appended straight to <body>, not this theme's code)
    // still rendered above the drawer, because it uses that same max
    // value and wins on DOM order (it's appended after page load, so it
    // lands later in the document than our server-rendered drawer). No
    // z-index number can win that fight. Instead: temporarily hide any
    // other fixed-position widget sitting directly on <body> while the
    // drawer is open — generic by design so it isn't tied to one
    // vendor's specific selector, which could change on its own with an
    // app update anyway.
    hideForeignFixedWidgets() {
        this.hiddenForeignWidgets = Array.from(document.body.children).filter((el) => {
            if (el === this) return false;
            if (el.closest && el.closest("custom-cart-drawer, quick-view-modal")) return false;
            return getComputedStyle(el).position === "fixed";
        });

        this.hiddenForeignWidgets.forEach((el) => {
            el.dataset.cartDrawerPrevVisibility = el.style.visibility || "";
            el.style.visibility = "hidden";
        });
    }

    restoreForeignFixedWidgets() {
        (this.hiddenForeignWidgets || []).forEach((el) => {
            el.style.visibility = el.dataset.cartDrawerPrevVisibility || "";
            delete el.dataset.cartDrawerPrevVisibility;
        });
        this.hiddenForeignWidgets = null;
    }

    cartRerender(event) {
        const sectionHTML = event.detail.sections["custom-cart-drawer"];
        const cartCountHtml = event.detail.sections["custom-cart-count"];

        /* ---------------- DRAWER UPDATE ---------------- */
        if (sectionHTML) {
            const parser = new DOMParser();
            const doc = parser.parseFromString(sectionHTML, "text/html");

            const newDrawer = doc.querySelector("custom-cart-drawer");

            if (newDrawer) {
                this.innerHTML = newDrawer.innerHTML;

                // re-bind after DOM replace
                this.overlay = this.querySelector("#CustomCartOverlay");
                this.closeButton = this.querySelector("[data-close]");
                this.liveRegion = this.querySelector("#CartLiveRegion");

                this.closeButton?.addEventListener(
                    "click",
                    this.closeDrawer.bind(this)
                );

                this.overlay?.addEventListener("click", () =>
                    this.closeDrawer()
                );

                this.openDrawer();
            }
        }

        /* ---------------- CART COUNT UPDATE ---------------- */
        let newCountText = null;
        if (cartCountHtml && this.cartCount) {
            const parser = new DOMParser();
            const countDoc = parser.parseFromString(cartCountHtml, "text/html");

            const newCount = countDoc.querySelector(".cart-items-count");

            if (newCount) {
                newCountText = newCount.textContent.trim();
                this.cartCount.textContent = newCountText;
            }
        }

        /* ---------------- ANNOUNCE TO SCREEN READERS ---------------- */
        if (this.liveRegion && newCountText !== null) {
            const count = parseInt(newCountText, 10) || 0;
            this.liveRegion.textContent =
                `Bag updated — ${count} item${count === 1 ? '' : 's'}.`;
        }
    }
}

customElements.define("custom-cart-drawer", CustomCartDrawer);

/*
-------------------------------------------------------------------
Variant-switch state, deliberately held here rather than on the
AtcButton instance.

Selecting a variant replaces .x-product__container, and the
<atc-button> lives inside it — so the element that started a fetch is
destroyed by that fetch's own response, and the replacement starts
life with fresh instance state. Anything guarding against overlapping
requests therefore has to outlive the element, or two successive
instances simply race each other again.
-------------------------------------------------------------------
*/
let pdpVariantRequestToken = 0;
let pdpVariantBusy = false;

class AtcButton extends HTMLElement {
    constructor() {
        super();        
        this.submitForm = this.querySelector("form[action='/cart/add']");
        this.handleSubmit = this.handleSubmit.bind(this);
    }


    get sectionId() {
        return this.dataset.sectionId;
    }

    connectedCallback() {

    // connectedCallback runs every time the element is inserted into the
    // document, including when it is merely moved — so without this guard
    // it bound a second, third, fourth set of listeners to the same
    // element. Each extra listener meant one tap on a swatch ran
    // handleChange again, firing another section fetch and another
    // innerHTML replacement. On a slow connection those responses landed
    // staggered, which is why the variant appeared to keep changing on its
    // own after you had stopped tapping.
    if (this.__atcBound) return;
    this.__atcBound = true;

    this.variantPicker =
    this.querySelectorAll('.x-product__variant-select');

    this.submitForm.addEventListener(
        "submit",
        this.handleSubmit
    );

    if (this.variantPicker.length > 0) {

        // Delegated from the element itself rather than bound to each
        // swatch, so the handler still works if the swatches inside are
        // re-rendered while this element stays put.
        this.addEventListener("click", (event) => {
            const swatch = event.target.closest('.opt-swatch');
            if (!swatch || !this.contains(swatch)) return;

            const group = swatch.closest('.x-product__variant-select');
            if (!group) return;

            // Ignore taps while a switch is already in flight. The options
            // are also visually disabled for that window (see the busy class
            // applied in handleChange), so this is the behaviour the UI is
            // already promising rather than a silent drop.
            if (pdpVariantBusy) return;

            // Re-selecting the swatch that is already active would kick off
            // another fetch and re-render for no change at all.
            if (swatch.classList.contains('active')) return;

            group.querySelectorAll('.opt-swatch').forEach((btn) => {
                btn.classList.remove('active');
                btn.setAttribute('aria-pressed', 'false');
            });

            swatch.classList.add('active');
            swatch.setAttribute('aria-pressed', 'true');

            this.handleChange();
        });

    }

}

    handleChange() {
    /*
    -----------------------------------
    GET SELECTED OPTIONS
    -----------------------------------
    */

    const selectedOptions = [];

    this.querySelectorAll('.x-product__variant-select')
    .forEach(group => {
        const active = group.querySelector('.opt-swatch.active');
        selectedOptions.push(active ? active.dataset.value : null);
    });

    /*
    -----------------------------------
    FIND MATCHING VARIANT
    -----------------------------------
    */

    const matchedVariant = window.productVariants.find(variant => {

        return variant.options.every((option, index) => {
            return option === selectedOptions[index];
        });

    });

    if (!matchedVariant) return;

    /*
    -----------------------------------
    UPDATE HIDDEN INPUT
    -----------------------------------
    */

    const hiddenInput = this.querySelector('input[name="id"]');

    if (hiddenInput) {
        hiddenInput.value = matchedVariant.id;
    }

    /*
    -----------------------------------
    UPDATE BUTTON STATE (sold out)
    -----------------------------------
    */

    const submitButton = this.querySelector('[data-add-to-bag]');

    if (submitButton) {
        submitButton.disabled = !matchedVariant.available;

        const btnText = submitButton.querySelector('.btn-text');
        if (btnText) {
            btnText.textContent = matchedVariant.available ? 'Add to Bag' : 'Sold Out';
        }
    }

    /*
    -----------------------------------
    UPDATE URL
    -----------------------------------
    */

    /*
    -----------------------------------
    PDP-ONLY: refresh the gallery/section.
    Skipped when this atc-button is reused outside the real product
    page (e.g. inside the Quick View modal), where `.x-product__container`
    doesn't exist — the hidden variant input above is already updated,
    so Add to Cart still works correctly without this step.
    -----------------------------------
    */
    if (!document.querySelector('.x-product__container')) return;

    const url = new URL(window.location.href);

    url.searchParams.set('variant', matchedVariant.id);

    const sectionUrl =
    `${url.pathname}${url.search}&section_id=${this.sectionId}`;

    /*
    -----------------------------------
    FETCH SECTION HTML
    -----------------------------------
    */

    /*
    -----------------------------------
    Only the newest request may paint. Tapping several swatches in a row
    leaves overlapping fetches in flight, and without this an earlier,
    slower response could land after a later one and put the page back onto
    a variant the shopper had already moved off.
    -----------------------------------
    */
    const requestToken = ++pdpVariantRequestToken;

    /*
    -----------------------------------
    Mark the options busy for the duration. The class drives the disabled
    styling; the flag stops further taps being queued. Set on the container
    rather than on this element because the container is what survives long
    enough to be cleaned up — and when the response does land it is replaced
    outright, which clears the class for free.
    -----------------------------------
    */
    pdpVariantBusy = true;
    document.querySelector('.x-product__container')
        ?.classList.add('is-variant-loading');

    const releaseBusy = () => {
        // A newer request has taken over; it owns the busy state now.
        if (requestToken !== pdpVariantRequestToken) return;
        pdpVariantBusy = false;
        document.querySelector('.x-product__container')
            ?.classList.remove('is-variant-loading');
    };

    fetch(sectionUrl)

    .then(response => response.text())

    .then(html => {

        if (requestToken !== pdpVariantRequestToken) return;

        const tempDiv = document.createElement('div');

        tempDiv.innerHTML = html;

        const newContainer =
        tempDiv.querySelector('.x-product__container');

        const liveContainer = document.querySelector('.x-product__container');

        if (!newContainer || !liveContainer) return;

        liveContainer.innerHTML = newContainer.innerHTML;

        /*
        -----------------------------------
        UPDATE URL
        -----------------------------------
        */

        window.history.replaceState({}, '', url.toString());

        /*
        -----------------------------------
        RE-INIT SWIPER
        -----------------------------------
        */

        requestAnimationFrame(() => {

            window.initializeProductGallery?.();

        });

    })

    // A failed switch leaves the page on the variant it already had, which
    // is the right outcome — but the options must not stay disabled, so
    // releasing happens either way.
    .catch(() => {})

    .finally(releaseBusy);

}
    
    handleSubmit(event) {
        event.preventDefault();

        const button = this.submitForm.querySelector('button[type="submit"]');

        // prevent double click
        if (button.classList.contains('loading')) return;

        button.classList.add('loading');

        let variantId = this.submitForm.querySelector('input[name="id"]')?.value;

        if(!variantId) {
            variantId = this.submitForm.querySelector('select[name="id"]')?.value;
        }

        let formData = {
            'items': [{
                'id': variantId,
                'quantity': this.submitForm.querySelector('input[name="quantity"]').value
            }],
            'sections': "custom-cart-drawer,custom-cart-count"
        };

        fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData),
        })
        .then(response => {
            return response.json();
        })
        .then(data => {
            document.documentElement.dispatchEvent(
                new CustomEvent("cart:rerender", {
                    detail: data,
                    bubbles: true
                })
            )
        })
        .catch((error) => {
            console.error('Error:', error);
        })
        .finally(() => {
            // restore button
            button.classList.remove('loading');
        });
    }

}

customElements.define("atc-button", AtcButton);

class CartActions extends HTMLElement {
    constructor() {
        super();

        this.plusButton = this.querySelector("[data-plus]")
        this.minusButton = this.querySelector("[data-minus]")
        this.removeButton = this.querySelector("[data-remove]")
    }

    connectedCallback() {
        this.plusButton.addEventListener("click", this.handleChange.bind(this))
        this.minusButton.addEventListener("click", this.handleChange.bind(this))
        this.removeButton.addEventListener("click", this.handleChange.bind(this))
    }

    handleChange(event) {

        const item = this.closest('.x-cart__item');

        // prevent multiple clicks
        if (item.classList.contains('loading')) return;

        item.classList.add('loading');

        const formData = {
            "line": this.dataset.line,
            // "quantity": event.target.dataset.quantity,
            quantity: event.target.closest('button').dataset.quantity,
            "sections": "custom-cart-drawer,custom-cart-count"
        }

        fetch(window.Shopify.routes.root + 'cart/change.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(formData),
        })
        .then(response => {
            return response.json();
        })
        .then(data => {
            document.documentElement.dispatchEvent(
                new CustomEvent("cart:rerender", {
                    detail: data,
                    bubbles: true
                })
            )
        })
        .catch((error) => {
            console.error('Error:', error);
        })
        .finally(() => {
            // fallback (in case rerender is slow)
            item.classList.remove('loading');
        });

    }
}

customElements.define("cart-actions", CartActions);